import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Loader2 } from 'lucide-react';

const LISBON_TZ = 'Europe/Lisbon';

/** Format a UTC ISO string as Europe/Lisbon date+time (24h, pt-PT). */
export function formatLisbon(isoUtc) {
  if (!isoUtc) return '';
  try {
    return new Intl.DateTimeFormat('pt-PT', {
      timeZone: LISBON_TZ,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(isoUtc));
  } catch { return isoUtc; }
}

/** Convert "YYYY-MM-DD" + "HH:MM" (Europe/Lisbon wall time) → UTC ISO. DST-safe. */
export function lisbonPartsToUtcIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  const naiveUtcMs = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
  const probe = new Date(naiveUtcMs);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: LISBON_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(probe).reduce((a, p) => (a[p.type] = p.value, a), {});
  const lisbonAsUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour === '24' ? '00' : parts.hour), Number(parts.minute), Number(parts.second),
  );
  const offsetMs = lisbonAsUtc - naiveUtcMs;
  return new Date(naiveUtcMs - offsetMs).toISOString();
}

/** Compute the default "date"/"time" strings (Lisbon) for an existing UTC ISO
 *  or "tomorrow 10:00" if none provided. */
export function lisbonDefaults(existingIsoUtc) {
  if (existingIsoUtc) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: LISBON_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date(existingIsoUtc)).reduce((a, p) => (a[p.type] = p.value, a), {});
    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      time: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}`,
    };
  }
  const t = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return { date: t.toISOString().slice(0, 10), time: '10:00' };
}

/**
 * Reusable dialog to pick a Europe/Lisbon date+time and return a UTC ISO string.
 *
 * @param {boolean}  open
 * @param {function} onOpenChange
 * @param {string}   title              Dialog title (default: "Agendar publicação")
 * @param {string}   description        Optional subtitle (e.g. content title)
 * @param {string?}  initialIso         Existing UTC ISO to prefill from
 * @param {string}   confirmLabel       Button label (default "Agendar publicação")
 * @param {function} onConfirm          async (isoUtc) => Promise — dialog stays busy until it resolves
 */
export default function SchedulePickerDialog({
  open,
  onOpenChange,
  title = 'Agendar publicação',
  description,
  initialIso,
  confirmLabel = 'Agendar publicação',
  onConfirm,
}) {
  const defaults = useMemo(() => lisbonDefaults(initialIso), [initialIso, open]);
  const [date, setDate] = useState(defaults.date);
  const [time, setTime] = useState(defaults.time);
  const [busy, setBusy] = useState(false);

  // Re-sync when dialog re-opens for a different item
  useMemo(() => {
    if (open) { setDate(defaults.date); setTime(defaults.time); }
    return null;
  }, [open, defaults.date, defaults.time]);

  const submit = async () => {
    const iso = lisbonPartsToUtcIso(date, time);
    if (!iso) { toast.error('Data ou hora inválida'); return; }
    if (new Date(iso).getTime() <= Date.now()) {
      toast.error('A data/hora tem de ser no futuro');
      return;
    }
    setBusy(true);
    try {
      await onConfirm(iso);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-surface border-border max-w-md" data-testid="sched-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">{title}</DialogTitle>
          <DialogDescription>
            {description && <span>{description}</span>}
            <span className="block text-xs mt-1 opacity-70">Fuso horário: {LISBON_TZ}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="sched-picker-date">Data</Label>
            <Input
              id="sched-picker-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="bg-background border-border"
              data-testid="sched-picker-date-input"
            />
          </div>
          <div>
            <Label htmlFor="sched-picker-time">Hora</Label>
            <Input
              id="sched-picker-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-background border-border"
              data-testid="sched-picker-time-input"
            />
          </div>
        </div>

        {date && time && (
          <div className="text-xs text-muted-foreground bg-background/50 rounded p-2 border border-border">
            Publica <span className="text-ruby">{formatLisbon(lisbonPartsToUtcIso(date, time))}</span> (Europe/Lisbon)
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy || !date || !time}
            className="bg-ruby hover:bg-ruby-light text-white"
            data-testid="sched-picker-confirm-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A processar…</> : (
              <><Calendar className="w-4 h-4 mr-2" />{confirmLabel}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
